/**
 * theme-core.js
 * Core bundle - Always loads immediately (blocking)
 * Contains essential functionality needed on every page
 * 
 * Size: ~180KB minified
 * Load: Synchronous, blocking
 */

// ============================================================================
// BOOTSTRAP JS - Selective imports for interactive components
// ============================================================================
import * as bootstrap from '../bootstrap.js';

// Make Bootstrap available globally
window.bootstrap = bootstrap;

// ============================================================================
// CORE GSAP - Always needed
// ============================================================================
// Only plugins used by core itself live here. Feature-specific plugins are
// bundled with their feature (SplitText -> theme-animations, Draggable +
// InertiaPlugin -> theme-slider-base). The GSAP plugin ESM files are
// self-contained and resolve the core via window.gsap, so no duplication.
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin);

// Global GSAP configuration
gsap.config({ autoSleep: 60 });
gsap.ticker.lagSmoothing(500, 33);

if (typeof ScrollTrigger !== 'undefined') {
  ScrollTrigger.config({
    limitCallbacks: true,
    ignoreMobileResize: true,
    autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load'
  });
}

// Make GSAP and core plugins globally available
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;
window.ScrollSmoother = ScrollSmoother;

// ============================================================================
// CORE MODULES
// ============================================================================
// sticky-header.js is not imported: logic.ky's header scrolls away (src/rebuild/ stage 02).

// ============================================================================
// GLOBAL VARIABLES & UTILITIES
// ============================================================================
let headerHeight, footerHeight;
const header = document.getElementById('wrapper-navbar');
const footer = document.getElementById('wrapper-footer');
let smoother;

const breakpoints = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
  xxxl: 1920
};

/**
 * Set CSS variables for header and footer heights
 */
function setCSSVariables() {
  if (header) {
    headerHeight = header.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight - 1}px`);
  }

  if (footer) {
    footerHeight = footer.offsetHeight;
    document.documentElement.style.setProperty('--footer-height', `${footerHeight - 1}px`);
  }
}

/**
 * Initialize form validation
 */
function initFormValidation() {
  const forms = document.querySelectorAll('form[data-validate]');
  if (forms.length === 0) return;

  forms.forEach(form => {
    form.addEventListener('submit', function(e) {
      const requiredFields = form.querySelectorAll('[required]');
      let isValid = true;

      requiredFields.forEach(field => {
        if (!field.value.trim()) {
          field.classList.add('is-invalid');
          isValid = false;
        } else {
          field.classList.remove('is-invalid');
        }
      });

      if (!isValid) {
        e.preventDefault();
      }
    });
  });
}

/**
 * Initialize anchor scrolling
 * Single delegated listener: cheaper than per-link listeners, covers
 * AJAX-injected content, and never duplicates on breakpoint re-entry.
 */
let anchorScrollingActive = false;

function initAnchorScrolling() {
  if (anchorScrollingActive) return;
  anchorScrollingActive = true;

  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (href === "#" || href === "#!") return;

    e.preventDefault();
    const target = document.getElementById(href.substring(1));

    if (target) {
      const offset = header ? header.offsetHeight + 10 : 90;

      gsap.to(window, {
        scrollTo: { y: target, offsetY: offset },
        duration: 0.8,
        ease: "power2.out"
      });
    }
  });
}

/**
 * Handle initial hash navigation
 */
function handleInitialAnchorScroll() {
  const hash = window.location.hash;
  if (!hash || hash === '#' || hash === '#!') return;

  setTimeout(() => {
    const target = document.querySelector(hash);
    if (target) {
      const offset = header ? header.offsetHeight + 10 : 90;
      
      if (smoother) {
        smoother.scrollTo(target, true, `top ${offset}px`);
      } else {
        gsap.to(window, {
          scrollTo: { y: target, offsetY: offset },
          duration: 0.8,
          ease: "power2.out"
        });
      }
    }
  }, 1000);
}

/**
 * Initialize ScrollSmoother
 */
function initScrollSmoother() {
  if (typeof ScrollSmoother === 'undefined') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.innerWidth < breakpoints.lg) return;

    // Safari's WebKit compositor conflicts with ScrollSmoother's transform-based scrolling
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (isSafari) {
    document.body.classList.add("smoother-disabled");
    return;
  }

  smoother = ScrollSmoother.create({
    smooth: 0.75,
    effects: true,
    ignoreMobileResize: true,
    normalizeScroll: false,
  });

  document.body.classList.add("smoother-active");
  
  // Initialize parallax backgrounds after smoother is ready (if animations bundle loaded)
  if (window.themeAnimations?.initParallaxBackgrounds) {
    window.themeAnimations.initParallaxBackgrounds();
    // Setup resize handler after parallax initialization
    window.themeAnimations.handleParallaxResize?.();
  }
  
  return smoother;
}

/**
 * Initialize mobile menu
 */
function initMobileMenu() {
  if (typeof gsap === 'undefined' || window.innerWidth >= breakpoints.lg) {
    return;
  }

  const navbarNav = document.getElementById('navbarNavDropdown');
  if (!navbarNav) return;

  const menuItems = navbarNav.querySelectorAll(':scope > ul > li');
  const menuTimeline = gsap.timeline({ paused: true });

  // Create staggered animation for menu items
  menuItems.forEach((item, index) => {
    menuTimeline.fromTo(item,
      { opacity: 0, x: 20 },
      { opacity: 1, x: 0, duration: 0.3, ease: "power2.out" },
      index * 0.1
    );
  });

  navbarNav.addEventListener('show.bs.collapse', () => {
    header.classList.add('mobile-opened');
    document.body.classList.add('overflow-hidden');
  });

  // Play animation when menu opens
  navbarNav.addEventListener('shown.bs.collapse', () => {
    menuTimeline.play();
  });

  // Reverse animation when menu closes
  navbarNav.addEventListener('hide.bs.collapse', () => {
    menuTimeline.reverse();
  });

  navbarNav.addEventListener('hidden.bs.collapse', () => {
    document.body.classList.remove('overflow-hidden');
    header.classList.remove('mobile-opened');
  });

  // Close mobile menu when anchor links are clicked (same-page navigation)
  const navLinks = navbarNav.querySelectorAll('a.nav-link:not(.dropdown-toggle), a.dropdown-item');
  navLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      // Only close for anchor links on the same page (exclude dummy '#' links)
      if (!href || !href.includes('#') || href === '#') return;
      const isAnchorOnly = href.startsWith('#');
      const isSamePageAnchor = href.startsWith(window.location.pathname + '#') || 
                               href.startsWith(window.location.origin + window.location.pathname + '#');
      if ((isAnchorOnly || isSamePageAnchor) && navbarNav.classList.contains('show')) {
        const bsCollapse = bootstrap.Collapse.getInstance(navbarNav);
        if (bsCollapse) {
          bsCollapse.hide();
        }
      }
    });
  });
}

/**
 * Initialize footer visibility trigger
 */
function initFooterTrigger() {
  if (typeof ScrollTrigger === 'undefined') return;

  ScrollTrigger.create({
    trigger: "#wrapper-footer",
    start: "top bottom",
    end: "bottom top",
    toggleClass: {
      targets: document.body,
      className: "footer-visible"
    }
  });
}

/**
 * Initialize Bootstrap tab handling for ScrollTrigger
 */
function initTabHandling() {
  // Listen for Bootstrap tab change events
  document.querySelectorAll('a[data-bs-toggle="tab"]').forEach(tab => {
    tab.addEventListener('shown.bs.tab', () => {
      // Refresh ScrollTrigger when tab changes
      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.refresh();
      }
    });
  });

  // Function to open a tab based on URL hash
  function openTabFromHash() {
    const hash = window.location.hash;
    if (hash) {
      const tabLink = document.querySelector(`.nav-link[data-bs-target="${hash}"]`);
      if (tabLink && typeof bootstrap !== 'undefined') {
        const tabInstance = new bootstrap.Tab(tabLink);
        tabInstance.show();
      }
    }
  }

  // Open the tab if the URL has a hash on page load
  const tabLinks = document.querySelectorAll('.nav-link[data-bs-target]');
  if (tabLinks.length > 0) {
    openTabFromHash();
  }
}

/**
 * Initialize with responsive breakpoints
 */
function initWithBreakpoints() {
  const mm = gsap.matchMedia();

  // Desktop initialization
  mm.add("(min-width: 992px)", () => {
    setCSSVariables(); // Run on breakpoint change
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!reduceMotion) {
      smoother = initScrollSmoother();
      initAnchorScrolling();
      handleInitialAnchorScroll();
    }
    
    initFooterTrigger();

    return () => {
      if (smoother) {
        smoother.kill();
        smoother = null;
      }
    };
  });

  // Mobile initialization
  mm.add("(max-width: 991px)", () => {
    setCSSVariables(); // Run on breakpoint change
    initMobileMenu();
    initFooterTrigger();
    document.body.classList.remove("smoother-active");
    
    // Reset parallax containers to default size on mobile
    if (window.themeAnimations?.resetParallaxContainers) {
      window.themeAnimations.resetParallaxContainers();
    }

    return () => {
      if (smoother) {
        smoother.kill();
        smoother = null;
      }
    };
  });
}

// ============================================================================
// CORE INITIALIZATION
// ============================================================================
function initCore() {
  initFormValidation();
  initTabHandling(); // Bootstrap tab handling for ScrollTrigger
  initWithBreakpoints(); // This calls setCSSVariables on breakpoint changes
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCore);
} else {
  initCore();
}

// ============================================================================
// EXPORTS - Make utilities available globally
// ============================================================================
window.themeCore = {
  breakpoints,
  getCSSVariables: () => ({ headerHeight, footerHeight }),
  setSmoother: (s) => { smoother = s; },
  getSmoother: () => smoother,
  refreshScrollTrigger: () => ScrollTrigger?.refresh(),
};
